import React from "react";

// Small presentational pieces shared by the forum pages.

const hash = (s: string) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

const initials = (name: string) =>
  name
    .replace(/[_.-]+/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("") || "?";

// Generated avatar: a stable two-tone gradient per name. The system account
// gets the brand bolt.
export const Avatar: React.FC<{ name: string | null; size?: number; system?: boolean; ring?: boolean }> = ({
  name,
  size = 36,
  system,
  ring,
}) => {
  const style: React.CSSProperties = { width: size, height: size, fontSize: size * 0.38 };
  if (system) {
    return (
      <span className={`fm-avatar is-system ${ring ? "has-ring" : ""}`} style={style} aria-hidden>
        <svg viewBox="0 0 32 32" width={size * 0.55} height={size * 0.55}>
          <path
            d="M18.5 2.2 L7.2 17.4 a0.9 0.9 0 0 0 0.72 1.44 H14.1 L11.6 29.1 a0.6 0.6 0 0 0 1.06 0.5 L24.86 14.5 a0.9 0.9 0 0 0 -0.72 -1.44 H17.9 L20.2 3.4 a0.6 0.6 0 0 0 -1.7 -1.2 z"
            fill="#fff"
          />
        </svg>
      </span>
    );
  }
  const h = hash(name ?? "?") % 360;
  style.background = `linear-gradient(135deg, hsl(${h} 72% 58%), hsl(${(h + 48) % 360} 68% 44%))`;
  return (
    <span className={`fm-avatar ${ring ? "has-ring" : ""}`} style={style} aria-hidden>
      {size < 28 ? initials(name ?? "?").slice(0, 1) : initials(name ?? "?")}
    </span>
  );
};

export const AvatarStack: React.FC<{ names: string[]; size?: number }> = ({ names, size = 24 }) => (
  <span className="fm-avatar-stack">
    {names.slice(0, 4).map((n) => (
      <Avatar key={n} name={n} size={size} system={n === "Spotprisprognos"} ring />
    ))}
  </span>
);

export const timeAgo = (iso: string, locale: string): string => {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const s = (new Date(iso).getTime() - Date.now()) / 1000;
  const abs = Math.abs(s);
  if (abs < 60) return rtf.format(0, "second");
  if (abs < 3600) return rtf.format(Math.round(s / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(s / 3600), "hour");
  if (abs < 7 * 86400) return rtf.format(Math.round(s / 86400), "day");
  return new Date(iso).toLocaleDateString(locale, { day: "numeric", month: "short" });
};

export const fullDate = (iso: string, locale: string) =>
  new Date(iso).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });

const ICONS: Record<string, React.ReactNode> = {
  bolt: <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />,
  chat: <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.4A8 8 0 1 1 21 12z" />,
  battery: (
    <>
      <rect x="2" y="7" width="17" height="10" rx="2" />
      <path d="M22 11v2M7 10l-2 3h4l-2 3" />
    </>
  ),
  doc: (
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5M9 13h6M9 17h4" />
    </>
  ),
  spark: <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8zM19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8z" />,
  pin: <path d="M9 4h6l-1 5 3 3v2H7v-2l3-3zM12 14v7" />,
  lock: (
    <>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </>
  ),
  reply: <path d="M9 14 4 9l5-5M4 9h10a6 6 0 0 1 6 6v5" />,
  share: (
    <>
      <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
      <path d="M16 6l-4-4-4 4M12 2v13" />
    </>
  ),
  more: (
    <>
      <circle cx="5" cy="12" r="1.4" />
      <circle cx="12" cy="12" r="1.4" />
      <circle cx="19" cy="12" r="1.4" />
    </>
  ),
  up: <path d="M12 5l7 8h-4.5v6h-5v-6H5z" />,
  comment: <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.4A8 8 0 1 1 21 12z" />,
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  shield: <path d="M12 3 4 6v6c0 5 3.4 8.3 8 9 4.6-.7 8-4 8-9V6z" />,
  flame: <path d="M12 3c1 4 5 5 5 10a5 5 0 0 1-10 0c0-2 1-3.5 2-4.5 0 2 1 3 2 3 0-3-1-5 1-8.5z" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  trophy: <path d="M8 4h8v5a4 4 0 0 1-8 0zM8 6H4a3 3 0 0 0 4 4M16 6h4a3 3 0 0 1-4 4M12 13v4M8 21h8M9 17h6" />,
  edit: <path d="M4 20h4L19 9l-4-4L4 16zM14 6l4 4" />,
  trash: <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" />,
  flag: <path d="M5 21V4M5 4h11l-2 4 2 4H5" />,
  eye: (
    <>
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  history: (
    <>
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 3v5h5M12 7v5l3 2" />
    </>
  ),
  check: <path d="m5 12 5 5L20 7" />,
};

export const Icon: React.FC<{ name: string; size?: number; className?: string; filled?: boolean }> = ({
  name,
  size = 16,
  className,
  filled,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={filled ? "currentColor" : "none"}
    stroke="currentColor"
    strokeWidth={filled ? 0 : 1.9}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden
  >
    {ICONS[name] ?? ICONS.chat}
  </svg>
);

export const CategoryChip: React.FC<{
  name: string;
  color?: string | null;
  icon?: string | null;
  onClick?: () => void;
}> = ({ name, color, icon, onClick }) => (
  <span
    className={`fm-chip ${onClick ? "is-clickable" : ""}`}
    style={{ "--chip": color ?? "#64748b" } as React.CSSProperties}
    onClick={onClick}
  >
    {icon && <Icon name={icon} size={12} />}
    {name}
  </span>
);
